import React from "react";

import { hydrateRoot, createRoot } from "react-dom/client";
import { v4 as uuidv4 } from "uuid";

import { getThemeStyles } from "@atlaskit/tokens";
import { MeResponse } from "@atlassiansox/asc-stargate";

import { ConfirmationPageOverlayVariants } from "@/templates/confirmation-page/src/hooks";

import {
    TemplateCollection,
    type Template
} from "./content/templates/template.types";
import { type AppPricingData } from "./contentful";
import { initializeFeatureGatesClient } from "./feature-gates/initialization";
import { Layout } from "./layout";
import { AisSitesType } from "./libraries/bxp-web-stargate-client/src";
import { UserLocale } from "./utils/useGeoCurrency";

window.BUILD_KEY = process.env["VERSION"];
window.featureGateBootstrapValues = window.featureGateBootstrapValues || {
    identifiers: {
        analyticsAnonymousId: uuidv4()
    }
};

declare global {
    type FeatureFlagType =
        | "boolean"
        | "string"
        | "number"
        | ConfirmationPageOverlayVariants;

    interface Window {
        IS_STATIC?: boolean;
        BUILD_KEY?: string;
        SSR_languagePack: Record<string, string>;
        prices?: AppPricingData;
        availableProducts?: AisSitesType[];
        userProfile?: MeResponse;
        templates?: Template[];
        collections?: TemplateCollection[];
        themeStyles: Awaited<ReturnType<typeof getThemeStyles>>;
        featureFlags: Record<string, FeatureFlagType>;
        countryCode?: UserLocale;
    }
}

export const init = (Component: React.ComponentType) => {
    const isStatic = typeof window !== "undefined" && window.IS_STATIC;
    initializeFeatureGatesClient(window.featureGateBootstrapValues).then(() => {
        if (isStatic) {
            // @ts-ignore
            const root = createRoot(document.getElementById("wac"));
            root.render(
                <Layout
                    location={window.location}
                    messages={window.SSR_languagePack}
                    pureClientMode={true}
                >
                    {Component}
                </Layout>
            );
        } else {
            const compiledCss = document.getElementById("ssr_style");
            hydrateRoot(
                document,
                <Layout
                    location={window.location}
                    messages={window.SSR_languagePack}
                    inlineCss={compiledCss?.textContent || ""}
                    pricing={window.prices}
                    availableProducts={window.availableProducts}
                    templates={window.templates}
                    collections={window.collections}
                    userProfile={window.userProfile}
                    countryCode={window.countryCode}
                    themeStyles={window.themeStyles}
                >
                    {Component}
                </Layout>
            );
        }
    });
};
